import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

export interface PropertyNavSection {
  id: string;
  label: string;
  icon: string;
  badge?: string | number;
}

export const DEFAULT_PROPERTY_NAV_SECTIONS: PropertyNavSection[] = [
  { id: 'definition-section', label: 'Définition', icon: 'badge' },
  { id: 'photos-section', label: 'Photos', icon: 'photo_camera' },
  { id: 'presentation-section', label: 'Présentation', icon: 'description' },
  { id: 'financials-section', label: 'Finances', icon: 'payments' },
  { id: 'key-infos-section', label: 'Infos clés', icon: 'tune' },
  { id: 'construction-section', label: 'Bâtiment', icon: 'home_work' },
  { id: 'characteristics-section', label: 'Pièces', icon: 'meeting_room' },
  { id: 'dpe-section', label: 'DPE', icon: 'bolt' },
  { id: 'geospatial-section', label: 'Géospatial', icon: 'travel_explore' },
  { id: 'location-section', label: 'Localisation', icon: 'location_on' },
  { id: 'cadastral-section', label: 'Cadastre', icon: 'grid_4x4' },
  { id: 'documents-section', label: 'Documents', icon: 'verified_user' },
];

export function getPropertyNavSections(options?: {
  hasMandate?: boolean;
  hasSettings?: boolean;
  isAdmin?: boolean;
  moderationBadge?: string | number;
}): PropertyNavSection[] {
  const sections: PropertyNavSection[] = [...DEFAULT_PROPERTY_NAV_SECTIONS];

  if (options?.hasMandate) {
    const keyInfosIndex = sections.findIndex((s) => s.id === 'key-infos-section');
    const mandateItem: PropertyNavSection = { id: 'mandate-section', label: 'Mandat', icon: 'handshake' };
    if (keyInfosIndex !== -1) {
      sections.splice(keyInfosIndex + 1, 0, mandateItem);
    } else {
      sections.push(mandateItem);
    }
  }

  if (options?.isAdmin) {
    sections.push({
      id: 'moderation-section',
      label: 'Modération',
      icon: 'policy',
      badge: options.moderationBadge,
    });
    sections.push({
      id: 'associated-users-section',
      label: 'Comptes associés',
      icon: 'people',
    });
  }

  return sections;
}

@Component({
  selector: 'etxe-property-section-nav',
  imports: [CommonModule, MatIconModule],
  templateUrl: './property-section-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class EtxePropertySectionNavComponent implements AfterViewInit {
  private destroyRef = inject(DestroyRef);
  private el = inject(ElementRef);

  sections = input<PropertyNavSection[]>(DEFAULT_PROPERTY_NAV_SECTIONS);
  variant = input<'horizontal' | 'timeline'>('horizontal');
  activeSection = signal<string>('definition-section');

  sectionSelected = output<string>();

  private isProgrammaticScroll = false;
  private scrollUnlockTimer?: ReturnType<typeof setTimeout>;

  ngAfterViewInit(): void {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (this.isProgrammaticScroll) {
          return;
        }
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeSection.set(entry.target.id);
          }
        }
      },
      {
        root: null,
        rootMargin: '-160px 0px -50% 0px',
        threshold: 0,
      },
    );

    // Observe all target section DOM nodes
    for (const sec of this.sections()) {
      const targetEl = document.getElementById(sec.id);
      if (targetEl) {
        observer.observe(targetEl);
      }
    }

    const onScrollEnd = () => {
      this.isProgrammaticScroll = false;
      if (this.scrollUnlockTimer) {
        clearTimeout(this.scrollUnlockTimer);
      }
    };

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', onScrollEnd);
    }

    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      if ('onscrollend' in window) {
        window.removeEventListener('scrollend', onScrollEnd);
      }
      if (this.scrollUnlockTimer) {
        clearTimeout(this.scrollUnlockTimer);
      }
    });
  }

  scrollToSection(anchorId: string): void {
    this.activeSection.set(anchorId);
    this.sectionSelected.emit(anchorId);

    // Prevent intermediate sections from flashing while smooth-scrolling to target
    this.isProgrammaticScroll = true;
    if (this.scrollUnlockTimer) {
      clearTimeout(this.scrollUnlockTimer);
    }
    this.scrollUnlockTimer = setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 800);

    const targetEl = document.getElementById(anchorId);
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
